import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Search, X, Briefcase, AlertTriangle, Building2, Users, Loader2 } from 'lucide-react';

const CATEGORY_CONFIG = {
  cases: { icon: Briefcase, label: 'Casos', color: 'text-blue-600', bg: 'bg-blue-50', path: (item, moduleSlug) => `/m/${moduleSlug}/caso/${item.id}` },
  alerts: { icon: AlertTriangle, label: 'Alertas', color: 'text-amber-600', bg: 'bg-amber-50', path: (item, moduleSlug) => `/m/${moduleSlug}/alerts/${item.id}` },
  empresas: { icon: Building2, label: 'Empresas', color: 'text-emerald-600', bg: 'bg-emerald-50', path: (item, moduleSlug) => `/m/clientes/empresa/${item.id}` },
  padron: { icon: Users, label: 'Padrón', color: 'text-violet-600', bg: 'bg-violet-50', path: (item, moduleSlug) => `/m/clientes/cliente/${item.cuit}` },
};

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  // Get current module slug from URL
  const moduleSlug = window.location.pathname.split('/m/')[1]?.split('/')[0] || 'monitoreo-transaccional';

  const search = useCallback(async (term) => {
    if (term.length < 2) { setResults(null); return; }
    setLoading(true);
    try {
      const res = await api.get(`/search/global?q=${encodeURIComponent(term)}&limit=5`);
      setResults(res.data);
    } catch { setResults(null); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 300);
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery('');
    setResults(null);
  };

  const handleSelect = (category, item) => {
    const cfg = CATEGORY_CONFIG[category];
    if (cfg) {
      navigate(cfg.path(item, moduleSlug));
      handleClose();
    }
  };

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) handleClose();
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Ctrl+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); handleOpen(); }
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const hasResults = results && results.total > 0;

  return (
    <div ref={containerRef} className="relative" data-testid="global-search">
      {/* Trigger button */}
      {!open && (
        <button
          onClick={handleOpen}
          data-testid="global-search-trigger"
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white/80 backdrop-blur-sm text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-all text-sm shadow-sm"
        >
          <Search size={15} />
          <span className="hidden sm:inline">Buscar...</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-slate-100 rounded border border-slate-200 text-slate-400 ml-2">
            Ctrl+K
          </kbd>
        </button>
      )}

      {/* Search input expanded */}
      {open && (
        <div className="absolute right-0 top-0 z-[100] w-[400px] max-w-[90vw]">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Input row */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
              <Search size={16} className="text-slate-400 flex-shrink-0" />
              <input
                ref={inputRef}
                data-testid="global-search-input"
                value={query}
                onChange={handleChange}
                placeholder="Buscar casos, alertas, empresas, CVUs..."
                className="flex-1 bg-transparent text-sm text-slate-800 placeholder:text-slate-400 outline-none"
              />
              {loading && <Loader2 size={16} className="text-blue-500 animate-spin flex-shrink-0" />}
              <button onClick={handleClose} className="text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            </div>

            {/* Results */}
            {results && (
              <div className="max-h-[360px] overflow-y-auto" data-testid="global-search-results">
                {!hasResults && query.length >= 2 && (
                  <div className="px-4 py-6 text-center text-sm text-slate-400">
                    Sin resultados para "{query}"
                  </div>
                )}
                {hasResults && Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
                  const items = results[key];
                  if (!items || items.length === 0) return null;
                  const Icon = cfg.icon;
                  return (
                    <div key={key}>
                      <div className="px-4 py-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider bg-slate-50">
                        {cfg.label} ({items.length})
                      </div>
                      {items.map((item) => (
                        <button
                          key={item.id || item.cuit}
                          onClick={() => handleSelect(key, item)}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left group"
                          data-testid={`search-result-${key}-${item.id || item.cuit}`}
                        >
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center ${cfg.bg} flex-shrink-0`}>
                            <Icon size={14} className={cfg.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate group-hover:text-slate-900">
                              {item.titulo || item.razon_social || item.nombre_padron || item.cuit || item.id}
                            </p>
                            <p className="text-xs text-slate-400 truncate">
                              {item.estado && <span className="mr-2">{item.estado}</span>}
                              {item.cuit && <span className="mr-2">CUIT: {item.cuit}</span>}
                              {item.severidad && <span className="mr-2">{item.severidad}</span>}
                              {item.cvu && <span>CVU: {item.cvu}</span>}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Footer hint */}
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">ESC para cerrar</span>
              {results && <span className="text-[11px] text-slate-400">{results.total} resultado(s)</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
